import {
  faChevronDown,
  faExternalLinkAlt,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo } from 'react';

import Link from 'next/link';
import { HelpfulButton } from '~/components/discussions/HelpfulButton';
import { CreatorReply } from '~/components/discussions/card/CreatorReply';
import ReviewInfo, {
  IDiscussionReviewInfo,
} from '~/components/discussions/card/DiscussionReviewInfo';
import DiscussionUserInfo, {
  IDiscussionUserInfoProps,
} from '~/components/discussions/card/DiscussionUserInfo';
import useCommentCard from '~/hooks/discussion/useCommentCard';
import { useUserStatus } from '~/hooks/userStatus';
import {
  DISCUSSION_STATUS_APPROVED,
  DISCUSSION_STATUS_REJECTED,
  DISCUSSION_STATUS_UNMODERATED,
  DiscussionStatus,
  ReviewV2,
} from '~/lib/discussions/types';
import useUser from '~/lib/user';
import { isIndexablePage } from '~/lib/util/constants';
import { CommentType, CommentV2Type } from '~/static/discussions/enums';
import { SubmitReply } from '../SubmitReply';
import DiscussionActions from './DiscussionActions';
import DiscussionOptions from './DiscussionOptions';
import DiscussionTag from './DiscussionTag';

export enum HelpfulClickEventTypes {
  UPVOTE = 'upvote',
  DOWNVOTE = 'downvote',
}

export function ReviewCard({
  review,
  partnersIds = [],
  mutateComment,
  showCollapseIcon = false,
  expandCard = false,
}: Readonly<{
  review: ReviewV2;
  partnersIds?: Array<number>;
  mutateComment?: () => void;
  showCollapseIcon?: boolean;
  expandCard?: boolean;
}>): JSX.Element {
  const [isCollapsed, setIsCollapsed] = React.useState(
    !expandCard && showCollapseIcon,
  );

  const {
    stateHelpfulCount,
    stateHasUserUpvoted,
    toggleVote,
    disableVoteBtn,
    creatorReply,
  } = useCommentCard(review, CommentV2Type.Review, partnersIds);

  const { user } = useUser();
  const { userStatus } = useUserStatus(review.deal_id);
  const { asPath } = useRouter();

  const isPartner = useMemo(() => {
    return partnersIds.includes(user?.id || 0);
  }, [partnersIds, user?.id]);

  const setCollapseValue = useCallback(() => {
    setIsCollapsed(!isCollapsed);
  }, [isCollapsed]);

  const wrapperClassName =
    'flex flex-col items-start gap-4 self-stretch rounded border border-gravel p-4 shadow-pdp relative';

  const userInfo: IDiscussionUserInfoProps = useMemo(
    () => ({
      username: review.user.username,
      avatar: review.user.avatar,
      isVerified: true, // we always now have verified users
      isPlus: review.user.has_plus ?? false,
      memberDate: new Date(review.user.date_joined),
      dealsBought: review.user.deals_purchased,
      userNamePadding: showCollapseIcon,
    }),
    [
      review.user.avatar,
      review.user.date_joined,
      review.user.deals_purchased,
      review.user.has_plus,
      review.user.username,
      showCollapseIcon,
    ],
  );

  const reviewInfo: IDiscussionReviewInfo = useMemo(
    () => ({
      rating: review.rating,
      publishDate: new Date(review?.created),
      dealPlan: '',
      title: review.title,
      content: review.comment,
      showText: !isCollapsed || !showCollapseIcon,
    }),
    [
      review.rating,
      review?.created,
      review.title,
      review.comment,
      isCollapsed,
      showCollapseIcon,
    ],
  );

  const showStatusAction = useMemo(() => {
    return (
      userStatus?.staff_moderator ||
      (review.status?.toLowerCase() !== DISCUSSION_STATUS_UNMODERATED &&
        (isPartner || user?.id === review.user.id))
    );
  }, [
    isPartner,
    review.status,
    review.user.id,
    user?.id,
    userStatus?.staff_moderator,
  ]);

  const showDiscussionTag = useMemo(() => {
    const isVisible =
      review.status?.toLowerCase() === DISCUSSION_STATUS_UNMODERATED ||
      review.status?.toLowerCase() === DISCUSSION_STATUS_APPROVED;

    return user?.id && isVisible && (!review.approved || !creatorReply);
  }, [review.status, review.approved, creatorReply, user?.id]);

  const showSubmitReply = useMemo(() => {
    const value =
      // Not visible if there's already a reply
      !creatorReply &&
      // If approved only visible for moderators
      ((isPartner &&
        review.status?.toLowerCase() === DISCUSSION_STATUS_APPROVED) ||
        // If declined only visible for staff
        (userStatus?.staff_moderator &&
          review.status?.toLowerCase() === DISCUSSION_STATUS_REJECTED));

    return value;
  }, [creatorReply, isPartner, review.status, userStatus?.staff_moderator]);

  useEffect(() => {
    if (!showCollapseIcon) {
      return;
    }

    setIsCollapsed(!expandCard);
  }, [expandCard, showCollapseIcon]);

  return (
    <div className={wrapperClassName} data-testid="review-card-wrapper">
      {/* Collapse icon */}
      <div className="absolute right-4 flex gap-3">
        {/* Only owner can see options */}
        {user?.id === review.user.id && (
          <DiscussionOptions
            deleteUrl={`/api/v2/deals/${review.deal_id}/reviews/${review.id}/`}
            discusstionType={CommentV2Type.Review}
            mutateComment={mutateComment}
          />
        )}
        {showCollapseIcon && (
          <button
            title="Collapse review"
            onClick={setCollapseValue}
            className="px-0.5"
            data-testid="review-collapse-icon"
          >
            <FontAwesomeIcon
              icon={faChevronDown}
              className={`${!isCollapsed && 'rotate-180'}`}
            />
          </button>
        )}
      </div>

      {showDiscussionTag && (
        <DiscussionTag
          isStaffOrPartner={userStatus?.user_moderator || isPartner}
          isOwner={user?.id === review.user.id}
          status={review.status?.toLowerCase() as DiscussionStatus}
          partnerHasReplied={!!creatorReply}
        />
      )}

      <DiscussionUserInfo {...userInfo} />

      <ReviewInfo {...reviewInfo} isCollapsed={isCollapsed} />

      {/* Collapsible content */}
      {(!isCollapsed || !showCollapseIcon) && (
        <>
          {!!creatorReply && (
            <CreatorReply {...creatorReply} mutateComment={mutateComment} />
          )}

          <div className="flex w-full items-center justify-between">
            <HelpfulButton
              helpfulCount={stateHelpfulCount}
              hasUserUpvoted={stateHasUserUpvoted}
              isDisabled={disableVoteBtn}
              onToggleVote={toggleVote}
            />

            {!isIndexablePage(asPath.split('?')[0]) && (
              <Link
                className="text-slate hover:text-gray-500"
                href={review.display_path}
                target="_blank"
              >
                <span className="sr-only">See detail</span>
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </Link>
            )}
          </div>

          {showStatusAction && (
            <DiscussionActions
              isModerator={userStatus?.staff_moderator ?? false}
              status={review.status?.toLowerCase() as DiscussionStatus}
              comment={review}
              mutateComment={mutateComment}
            />
          )}

          {showSubmitReply && (
            <SubmitReply
              dealId={review.deal_id}
              comment={review}
              mutateComment={mutateComment}
              type={CommentType.Review}
            />
          )}
        </>
      )}

      {showStatusAction && isCollapsed && (
        <DiscussionActions
          isModerator={userStatus?.staff_moderator ?? false}
          status={review.status?.toLowerCase() as DiscussionStatus}
          comment={review}
          mutateComment={mutateComment}
        />
      )}
    </div>
  );
}
